import { zodResolver } from '@hookform/resolvers/zod'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DeleteIcon } from '@repo/ui/icons'
import { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { createTransferMeal } from '../../../api/transfers/create-transfer-meal'
import { previewTransferMeal } from '../../../api/transfers/preview-transfer-meal'
import { updateTransferMealOrder } from '../../../api/transfers/transfer-meal-orders'
import { getTripProviders } from '../../../api/trip-providers/get-trip-providers'
import { TransferPlace } from '../../../lib/types/models/transfer/transfer-place'
import { CreateTransferMealInput } from '../../../lib/types/models/transfer-meal/create-transfer-meal'
import {
  TransferMealLine,
  TransferMealOrder
} from '../../../lib/types/models/transfer-meal/transfer-meal'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringArraySchema } from '../../../lib/utils/validations/string-validation'
import { queryClient } from '../../../main'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import ActionButtons from '../../elements/action-buttons'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'

interface TripFolderOrderMealFormProps {
  folderId: number
  // new order for the selected transfers
  transfers?: TransferPlace[]
  // sent order being edited
  order?: TransferMealOrder
  toggleModal: () => void
}

const schema = z.object({
  tripProviderId: createNumberSchema('prestataire'),
  description: z.string().nullish(),
  cc: createStringArraySchema('cc', { isEmail: true }).nullish(),
  bcc: createStringArraySchema('bcc', { isEmail: true }).nullish(),
  lines: z
    .array(
      z.object({
        transferPlaceId: z.number(),
        numberOfMeals: createNumberSchema('nombre de repas', {
          isInteger: true,
          nonNegative: true
        })
      })
    )
    .min(1)
})

const toLineInputs = (lines: TransferMealLine[]) =>
  lines.map(({ transferPlaceId, numberOfMeals }) => ({ transferPlaceId, numberOfMeals }))

const TripFolderOrderMealForm = ({
  transfers,
  order,
  folderId,
  toggleModal
}: TripFolderOrderMealFormProps) => {
  const isEdit = !!order

  const form = useForm<CreateTransferMealInput>({
    defaultValues: {
      tripProviderId: order?.tripProvider.id,
      description: order?.description ?? '',
      cc: order?.cc ?? [],
      bcc: order?.bcc ?? [],
      lines: order ? toLineInputs(order.lines) : []
    },
    mode: FORM_MODE,
    resolver: zodResolver(schema)
  })

  const { data: tripProviders } = useQuery({
    queryKey: ['trip-providers'],
    queryFn: () => getTripProviders({ version: 'LIGHT' })
  })

  // the meals per transfer are computed by the backend, the admin can still change them
  const {
    data: previewLines,
    isPending: isPreviewPending,
    error: previewError
  } = useQuery({
    queryKey: ['transfer-meal-preview', folderId, transfers?.map((transfer) => transfer.id)],
    queryFn: () =>
      previewTransferMeal(
        folderId,
        (transfers ?? []).map((transfer) => transfer.id)
      ),
    enabled: !isEdit && !!transfers?.length
  })

  const { fields, replace, remove } = useFieldArray({ control: form.control, name: 'lines' })

  useEffect(() => {
    if (previewLines) replace(toLineInputs(previewLines))
  }, [previewLines, replace])

  // e.g. a selected transfer is already on another meal order
  useEffect(() => {
    if (previewError) showAxiosError(previewError)
  }, [previewError])

  // delivery, address and station of each transfer, shown next to its meal count
  const linesInfo = new Map(
    (order?.lines ?? previewLines ?? []).map((line) => [line.transferPlaceId, line])
  )

  const onSuccess = ({ message }: { message: string }) => {
    toast.success(message)
    queryClient.invalidateQueries({ queryKey: ['trip-folder-transfers', folderId] })
    if (order) queryClient.invalidateQueries({ queryKey: ['transfer-meal-order', order.id] })
    toggleModal()
  }

  const { mutate: create, isPending: isCreatePending } = useMutation({
    mutationFn: createTransferMeal,
    onSuccess,
    onError: showAxiosError
  })

  const { mutate: update, isPending: isUpdatePending } = useMutation({
    mutationFn: updateTransferMealOrder,
    onSuccess,
    onError: showAxiosError
  })

  const handleSubmit = ({
    tripProviderId,
    description,
    cc,
    bcc,
    lines
  }: CreateTransferMealInput) => {
    const input = {
      description: description ?? '',
      cc: cc ?? [],
      bcc: bcc ?? [],
      lines
    }

    if (order) update({ id: order.id, ...input })
    else create({ id: folderId, tripProviderId, ...input })
  }

  const selectedTripProvider = tripProviders?.data.find(
    (tripProvider) => tripProvider.id === form.watch('tripProviderId')
  )
  const contacts = selectedTripProvider?.contacts.map((contact) => contact.email) ?? []

  return (
    <Form {...form}>
      <form className={MODAL_BODY_CLASSNAME} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name={'tripProviderId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label={'Prestataire Voyage'}
                    required
                    searchable
                    // the provider of a sent order is fixed: cancel it and order again instead
                    disabled={isEdit}
                    options={tripProviders?.data.map((tripProvider) => ({
                      label: tripProvider.companyName,
                      value: tripProvider.id
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label:
                        selectedTripProvider?.companyName ?? order?.tripProvider.companyName ?? '',
                      value: field.value
                    })}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex items-center gap-1">
            <Typography variant="label-medium">À :</Typography>
            <div className="flex flex-wrap gap-1">
              {contacts.length > 0
                ? contacts.map((contact, index) => (
                    <Badge key={index} text={contact} variant="grey" />
                  ))
                : form.watch('tripProviderId') && <Badge text="Aucun contact" variant="danger" />}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'cc'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    type="text"
                    label="CC"
                    variant="primary"
                    onChange={field.onChange}
                    selectedOptions={field.value?.map((cc) => ({ label: cc, value: cc })) ?? []}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'bcc'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    type="text"
                    label="BCC"
                    variant="primary"
                    onChange={field.onChange}
                    selectedOptions={field.value?.map((cc) => ({ label: cc, value: cc })) ?? []}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-2">
            <Typography variant="label-semibold">Répartition des repas</Typography>
            {previewError ? (
              <Badge
                text="Impossible de préparer la commande pour ces transferts"
                variant="danger"
              />
            ) : !isEdit && isPreviewPending ? (
              <Skeleton className="h-[100px] w-full" />
            ) : (
              fields.map((field, index) => {
                const line = linesInfo.get(field.transferPlaceId)
                if (!line) return null

                return (
                  <div
                    key={field.id}
                    className="flex items-center gap-4 rounded-xl border border-grey-light p-3"
                  >
                    <div className="flex flex-1 flex-col gap-1">
                      <Typography variant="label-semibold">
                        {line.date} - {line.time}
                      </Typography>
                      <Typography variant="secondary-body">{line.address}</Typography>
                      <Typography variant="secondary-body">Gare : {line.station}</Typography>
                    </div>
                    <FormField
                      control={form.control}
                      name={`lines.${index}.numberOfMeals`}
                      render={({ field }) => (
                        <FormItem className={'w-32'}>
                          <FormControl>
                            <ControlledInput {...field} type="number" label="Repas" required />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        icon={DeleteIcon}
                        variant="ghost"
                        className="w-fit h-fit px-1.5 py-1"
                        onClick={() => remove(index)}
                      />
                    )}
                  </div>
                )
              })
            )}
          </div>

          <FormField
            control={form.control}
            name={'description'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledTextEditor
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Message au prestataire (optionnel)"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </form>
      <ActionButtons
        isLoading={isCreatePending || isUpdatePending}
        disabled={!!previewError || fields.length === 0}
        onCancel={toggleModal}
        onSubmit={() => {
          form.handleSubmit(handleSubmit)()
        }}
      />
    </Form>
  )
}

export default TripFolderOrderMealForm
